import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const flashAttentionComponent: ComponentDef = {
  id: 'flash-attention',
  name: 'Flash Attention',
  description: 'IO-aware attention: tiles Q/K/V into SRAM blocks and fuses softmax online to avoid materialising the full N×N matrix in HBM. FA-3 adds warp-specialization and WGMMA+TMA overlap for H100.',
  category: 'optimization',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'n_heads', label: 'Heads', type: 'number', default: 8, min: 1, max: 128, step: 1 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 1024, min: 1, max: 8192, step: 1 },
    {
      name: 'version',
      label: 'Version',
      type: 'select',
      default: 'fa2',
      options: [
        { label: 'FA-1 (forward tiling)', value: 'fa1' },
        { label: 'FA-2 (work partition, no transpose)', value: 'fa2' },
        { label: 'FA-3 (warp-specialized, WGMMA+TMA, H100)', value: 'fa3' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const n_heads = Number(params.n_heads);
    const seq_len = Number(params.seq_len);
    const d_head = d_model / n_heads;
    const version = String(params.version || 'fa2');
    // Tile sizes: Br rows of Q, Bc cols of K/V that fit in SRAM
    const Br = Math.min(64, seq_len);
    const Bc = Math.min(64, seq_len);

    const g = new GraphBuilder();
    g.input('x', [seq_len, d_model]);

    // ── Project Q / K / V (once, load from HBM) ──
    g.linear('wq', 'x', [d_model, d_model]);
    g.linear('wk', 'x', [d_model, d_model]);
    g.linear('wv', 'x', [d_model, d_model]);
    g.reshape('Q', 'wq', [n_heads, seq_len, d_head]);
    g.reshape('K', 'wk', [n_heads, seq_len, d_head]);
    g.reshape('V', 'wv', [n_heads, seq_len, d_head]);

    // ── SRAM block loading (one tile per iteration) ──
    g.reshape('Q_tile', 'Q', [n_heads, Br, d_head]);
    g.reshape('K_tile', 'K', [n_heads, Bc, d_head]);
    g.reshape('V_tile', 'V', [n_heads, Bc, d_head]);

    if (version === 'fa3') {
      // FA-3: TMA async loads K/V tiles while WGMMA runs on previous tile
      g.activation('K_tile_T', 'K_tile', 'TMA async load  K_j → SRAM');
      g.matmul('S_ij', 'Q_tile', 'K_tile_T', [n_heads, Br, Bc]);
      g.scale('S_scaled', 'S_ij', '1/√d_k');
      // Warp-specialization: producer warps prefetch, consumer warps execute WGMMA
      g.activation('warp_prod', 'S_scaled', 'Producer warps: TMA prefetch next K/V tile');
      g.activation('warp_cons', 'warp_prod', 'Consumer warps: WGMMA (Warpgroup MMA)');
      // Two-stage softmax pipeline: rescale happens in parallel with next tile GEMM
      g.activation('m_new', 'warp_cons', 'rowmax(S_ij)  — running max (pipelined)');
      g.activation('P_ij', 'warp_cons', 'exp(S_ij − m_new)');
      g.activation('l_new', 'P_ij', 'rowsum(P_ij)  — running sum');
      g.scale('rescale', 'P_ij', 'l_prev·exp(m_prev−m_new)  [2-stage pipeline]');
      g.matmul('O_tile', 'rescale', 'V_tile', [n_heads, Br, d_head]);
      g.scale('O_norm', 'O_tile', '÷ l_new  (final normalisation)');
    } else {
      // FA-1/FA-2: standard online softmax with sequential tiling
      g.transpose('K_tile_T', 'K_tile', [n_heads, d_head, Bc], 'K_j^T');
      g.matmul('S_ij', 'Q_tile', 'K_tile_T', [n_heads, Br, Bc]);
      g.scale('S_scaled', 'S_ij', '1/√d_k');
      // Online softmax: running max m_i and sum l_i (no full N×N materialisation)
      g.activation('m_new', 'S_scaled', 'rowmax(S_ij)  — running max');
      g.activation('P_ij', 'S_scaled', 'exp(S_ij − m_new)');
      g.activation('l_new', 'P_ij', 'rowsum(P_ij)  — running sum');
      g.scale('rescale', 'P_ij', 'l_prev·exp(m_prev−m_new)');
      g.matmul('O_tile', 'rescale', 'V_tile', [n_heads, Br, d_head]);
      g.scale('O_norm', 'O_tile', '÷ l_new  (final normalisation)');
    }

    // ── Reassemble and project ──
    g.reshape('O_full', 'O_norm', [n_heads, seq_len, d_head]);
    g.reshape('concat', 'O_full', [seq_len, d_model]);
    g.linear('wo', 'concat', [d_model, d_model]);
    g.output('out', 'wo');
    return g.build();
  }
};
